/**
 * 注文書の2回目 2a（#233、docs/93 §14.3・§14.4、★8）の画面の部品：
 *   ・まとめて作成（見積依頼の一覧・{vendorQuote}の一覧で行を選ぶ帯。2件以上で「まとめて作成」・1件は「{order}作成」）
 *   ・2枚目の確かめ（作成済みの{order}があれば「PO-0001 を作成済みです。もう1枚作りますか」＝止めない）
 *   ・採用／不採用（印だけ・何度でも・確かめの窓なし）・回答の比較・辞退の連絡（任意）
 *   ・発注済のしるし・見積の行の「{order}に追加…」の窓
 * 文は contract の ORDER_BUNDLE_MSG 1か所（{order} などは t() を通す）
 */
import {
  ESTIMATE_PC_MSG,
  ORDER_BUNDLE_MSG as M,
  ORDER_MSG,
  alreadyOrderedText,
  orderedMarkText,
  type DeclineMailDraft,
  type DeclineMailResult,
  type DeclineMailSendInput,
  type OrderBulkCreateInput,
  type OrderBulkResult,
  type OrderList,
  type OrderRef,
  type VendorQuoteAdoption,
  type VendorQuoteCompareItem,
} from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, messageOf } from "../../api.js";
import { ErrorText, Help, Switch } from "../../components/ui.jsx";
import { t } from "../../labels.js";
import { yen } from "../../lib/num.js";
import { md } from "../rfq/parts.jsx";

/** 発注済のしるし：「発注済 PO-0001」（下書きだけなら「{order}下書き」・2件目からは「ほか n件」）。乗せると全部の番号 */
export function OrderedMark({ refs, testId = "ordered-mark" }: { refs: ReadonlyArray<OrderRef>; testId?: string }) {
  if (!refs.length) return null;
  const title = refs.map((r) => r.no ?? t(M.alreadyDraft)).join("\n");
  return (
    <span className="chip ordmark" title={title} data-testid={testId}>
      {t(orderedMarkText(refs))}
    </span>
  );
}

/** 2枚目の確かめ（止めない）：「PO-0001 を作成済みです。もう1枚作りますか」 */
export function AlreadyDialog({ refs, busy, onOk, onClose }: { refs: ReadonlyArray<OrderRef>; busy: boolean; onOk: () => void; onClose: () => void }) {
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="dlg" role="dialog" aria-label={t(M.alreadyTitle)} data-testid="order-already-dialog" onKeyDown={(e) => e.key === "Escape" && !busy && onClose()}>
        <div className="dh">{t(M.alreadyTitle)}</div>
        <div className="db">
          <p style={{ margin: 0 }}>{t(alreadyOrderedText(refs))}</p>
        </div>
        <div className="df">
          <button type="button" className="btn" disabled={busy} onClick={onClose}>取消</button>
          <button type="button" className="btn primary" disabled={busy} autoFocus onClick={onOk} data-testid="order-already-ok">{M.alreadyDo}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * まとめて作成・1件の「{order}作成」：作成済みの{order}があれば確かめてから作る。
 * 作ったら 1枚＝その作成画面・2枚以上＝{order}の下書きの一覧（通知「3枚の下書きを作成しました」）
 */
export function useOrderBundle() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [ask, setAsk] = useState<{ req: OrderBulkCreateInput; refs: OrderRef[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (req: OrderBulkCreateInput) => {
    setBusy(true);
    try {
      const out = await api<OrderBulkResult>("POST", "/orders/bulk", req);
      setAsk(null);
      void qc.invalidateQueries({ queryKey: ["orders"] });
      void qc.invalidateQueries({ queryKey: ["rfqs"] });
      void qc.invalidateQueries({ queryKey: ["vql"] });
      const one = out.orders.length === 1 ? out.orders[0] : undefined;
      if (one) nav(`/orders/${one.id}`, { state: { notice: t(M.createdOne), noticeTestId: "order-bundle-notice" } });
      else nav("/orders?tab=draft", { state: { notice: M.created.replace("{n}", String(out.orders.length)), noticeTestId: "order-bundle-notice" } });
    } catch (e) {
      setAsk(null);
      throw e;
    } finally {
      setBusy(false);
    }
  };
  /** refs＝選んだものから作成済みの{order}（あれば確かめる） */
  const start = (req: OrderBulkCreateInput, refs: ReadonlyArray<OrderRef>): Promise<void> => {
    const uniq = [...new Map(refs.map((r) => [r.id, r])).values()];
    if (uniq.length) {
      setAsk({ req, refs: uniq });
      return Promise.resolve();
    }
    return run(req);
  };
  const dialog = ask ? <AlreadyDialog refs={ask.refs} busy={busy} onOk={() => void run(ask.req).catch(() => undefined)} onClose={() => setAsk(null)} /> : null;
  return { start, run, busy, dialog };
}

/** 選んだ行の帯（見積依頼の一覧・{vendorQuote}の一覧）。2件以上で「まとめて作成」・1件は「{order}作成」 */
export function BundleBar({ count, busy, onCreate, onClear, children }: { count: number; busy: boolean; onCreate: () => void; onClear: () => void; children?: React.ReactNode }) {
  if (!count) return null;
  return (
    <div className="obundle" role="region" aria-label={M.selected.replace("{n}", String(count))} data-testid="bundle-bar" data-notice-above>
      <b className="num" data-testid="bundle-count">{M.selected.replace("{n}", String(count))}</b>
      {children}
      <span className="sp" />
      <button type="button" className="btn text" disabled={busy} onClick={onClear} data-testid="bundle-clear">{M.clearSelection}</button>
      <button type="button" className="btn primary" disabled={busy} onClick={onCreate} data-testid="bundle-create">
        {count >= 2 ? M.bundle : t(ORDER_MSG.create)}
      </button>
    </div>
  );
}

/** 行を選ぶ □（一覧の左端）。押しても行は開かない */
export function SelectBox({ on, label, disabled, onChange, testId = "bundle-select" }: { on: boolean; label: string; disabled?: boolean; onChange: (v: boolean) => void; testId?: string }) {
  return (
    <input
      type="checkbox"
      className="obsel"
      checked={on}
      disabled={disabled}
      aria-label={`${label}を${M.selectRow}`}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      onChange={(e) => onChange(e.target.checked)}
      data-testid={testId}
    />
  );
}

/** 採用・不採用のしるし（一覧のチップ。不採用は薄墨） */
export function AdoptionChip({ value, testId = "adoption-chip" }: { value: VendorQuoteAdoption | null | undefined; testId?: string }) {
  if (!value) return null;
  return (
    <span className={"chip adopt" + (value === "declined" ? " is-declined" : " is-adopted")} data-testid={testId} data-adoption={value}>
      {value === "adopted" ? M.adoptedChip : M.declinedChip}
    </span>
  );
}

/** 採用／不採用の切り替え（印だけ）。いまの方を押すと未選択に戻す。反映のある回答は不採用にできない */
export function AdoptionToggle({ value, used, busy, onChange, testId = "adoption", compact }: { value: VendorQuoteAdoption | null; used: boolean; busy: boolean; onChange: (v: VendorQuoteAdoption | null) => void; testId?: string; compact?: boolean }) {
  const btn = (v: VendorQuoteAdoption, label: string) => {
    const on = value === v;
    const blocked = v === "declined" && used && !on;
    return (
      <button
        type="button"
        className={"adoptbtn" + (on ? " is-on" : "") + (v === "declined" ? " is-declined" : "")}
        aria-pressed={on}
        disabled={busy || blocked}
        title={blocked ? "反映した行を解除すると不採用にできます" : undefined}
        onClick={() => onChange(on ? null : v)}
        data-testid={`${testId}-${v === "adopted" ? "adopt" : "decline"}`}
      >
        {label}
      </button>
    );
  };
  return (
    <span className={"adopttog" + (compact ? " is-compact" : "")} role="group" aria-label={M.adoptionGroup} data-testid={testId}>
      {btn("adopted", M.adopt)}
      {btn("declined", M.decline)}
    </span>
  );
}

/** 回答の比較（同じ{project}・同じ件名の見積依頼への回答）：{vendor}・依頼・金額（税抜）・回答日・採用の印、右端で採用・不採用 */
export function CompareBand({ items, busy, onChange }: { items: ReadonlyArray<VendorQuoteCompareItem>; busy: boolean; onChange: (id: string, v: VendorQuoteAdoption | null, prev: VendorQuoteAdoption | null) => void }) {
  if (items.length < 2) return null;
  return (
    <section className="vqcmp" aria-label={M.compareTitle} data-testid="vq-compare">
      <div className="vqcmp-h">
        <b>{M.compareTitle}</b>
        <span className="muted num">{items.length}件</span>
        <Help label={M.compareTitle} text={t(M.compareHelp)} testId="help-vq-compare" />
      </div>
      <table className="vqcmp-t">
        <thead>
          <tr>
            <th>{t(M.compareVendor)}</th>
            <th>依頼</th>
            <th className="r">{M.compareAmount}</th>
            <th>{M.compareDate}</th>
            <th>{M.compareMark}</th>
            <th aria-label={M.adoptionGroup} />
          </tr>
        </thead>
        <tbody>
          {items.map((x) => (
            <tr key={x.vendorQuoteId} className={x.current ? "is-current" : undefined} data-testid="vq-compare-row">
              <td>
                {x.current ? (
                  <b>{x.vendorName}</b>
                ) : (
                  <Link to={`/vendor-quotes/${x.vendorQuoteId}`} data-testid="vq-compare-open">{x.vendorName}</Link>
                )}
                {x.current ? <span className="muted">（{M.compareCurrent}）</span> : null}
              </td>
              <td className="num">{x.rfqNo ?? "—"}</td>
              <td className="r num">{x.totalExTax == null ? "—" : yen(x.totalExTax)}</td>
              <td className="num">{md(x.answeredOn)}</td>
              <td><AdoptionChip value={x.adoption} testId="vq-compare-chip" /></td>
              <td className="r">
                <AdoptionToggle value={x.adoption} used={x.used} busy={busy} compact onChange={(v) => onChange(x.vendorQuoteId, v, x.adoption)} testId="vq-compare-adoption" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** 辞退の連絡（任意）：宛先・件名・本文（差し込みを埋めた初めの値）・控え Bcc → 送信。送付の記録に残る */
export function DeclineMailDialog({ vendorQuoteId, onClose, onSent }: { vendorQuoteId: string; onClose: () => void; onSent: () => void }) {
  const d = useQuery({ queryKey: ["decline-mail", vendorQuoteId], queryFn: () => api<DeclineMailDraft>("GET", `/vendor-quotes/${vendorQuoteId}/decline-mail`), retry: false, gcTime: 0 });
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [bccSelf, setBccSelf] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  useEffect(() => {
    if (!d.data) return;
    setTo(d.data.to ?? "");
    setSubject(d.data.subject);
    setBody(d.data.body);
  }, [d.data]);
  const send = async () => {
    setBusy(true);
    setErr(undefined);
    try {
      await api<DeclineMailResult>("POST", `/vendor-quotes/${vendorQuoteId}/decline-mail`, { to: [to.trim()], subject, body, bccSelf } satisfies DeclineMailSendInput);
      onSent();
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <form
        className="dlg decmail"
        role="dialog"
        aria-label={M.declineMailTitle}
        data-testid="decline-mail-dialog"
        onKeyDown={(e) => e.key === "Escape" && !busy && onClose()}
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <div className="dh">
          {M.declineMailTitle}
          <Help label={M.declineMailTitle} text={t(M.declineMailHelp)} testId="help-decline-mail" />
        </div>
        <div className="db">
          {d.isError ? <ErrorText>{messageOf(d.error)}</ErrorText> : null}
          {d.data?.sent ? <p className="hint" style={{ marginTop: 0 }} data-testid="decline-mail-sent">{M.declineSentAt.replace("{md}", md(d.data.sent.at.slice(0, 10)))}</p> : null}
          <label className="vql-fld">
            <span>{M.declineTo}</span>
            <input type="email" value={to} disabled={!d.data} autoComplete="off" onChange={(e) => setTo(e.target.value)} data-testid="decline-mail-to" />
          </label>
          <label className="vql-fld">
            <span>{M.declineSubject}</span>
            <input value={subject} disabled={!d.data} maxLength={200} onChange={(e) => setSubject(e.target.value)} data-testid="decline-mail-subject" />
          </label>
          <label className="vql-fld">
            <span>{M.declineBody}</span>
            <textarea value={body} disabled={!d.data} rows={9} maxLength={4000} onChange={(e) => setBody(e.target.value)} data-testid="decline-mail-body" />
          </label>
          <Switch checked={bccSelf} onChange={setBccSelf} label={M.declineBccSelf} testId="decline-mail-bcc" />
          <ErrorText>{err}</ErrorText>
        </div>
        <div className="df">
          <button type="button" className="btn" disabled={busy} onClick={onClose}>取消</button>
          <button type="submit" className="btn primary" disabled={busy || !d.data || !to.trim() || !subject.trim() || !body.trim()} data-testid="decline-mail-send">
            {busy ? M.declineSending : M.declineSend}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * 見積の行の「…」→「{order}に追加…」（#233 2a）：{vendor}と追加先（その{vendor}の{order}の下書き／新しい{order}）を選ぶ → その行を写す。
 * 見積依頼の「見積依頼に追加…」と同じ形の窓（単価は行の仕入れ・税区分は行のまま）
 */
export function OrderAddLineDialog({ lineName, value, vendors, customer, onApply, onClose }: {
  lineName: string;
  /** 行の外注先（既定に選ぶ） */
  value: string | null;
  vendors: Array<{ id: string; name: string; full: string; label: string }>;
  customer: string;
  onApply: (vendorId: string, orderId: string | null) => void;
  onClose: () => void;
}) {
  const [v, setV] = useState<string>(value ?? vendors[0]?.id ?? "");
  const [target, setTarget] = useState<string>("");
  const drafts = useQuery({ queryKey: ["orders", "draft", v], queryFn: () => api<OrderList>("GET", `/orders?tab=draft&vendor=${v}`), enabled: !!v });
  const mine = (drafts.data?.items ?? []).filter((x) => x.vendorId === v);
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="dlg"
        role="dialog"
        aria-label={t(M.addToOrderTitle)}
        data-testid="order-add-dialog"
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        onSubmit={(e) => {
          e.preventDefault();
          if (v) onApply(v, target || null);
        }}
      >
        <div className="dh">{t(M.addToOrderTitle)}</div>
        <div className="db">
          <p style={{ marginTop: 0 }}>
            <b>{lineName || t("（{lineName}なし）")}</b>
          </p>
          {vendors.length ? (
            <>
              <label className="vql-fld">
                <span>{t("{vendor}")}</span>
                <select value={v} autoFocus onChange={(e) => { setV(e.target.value); setTarget(""); }} data-testid="order-add-vendor">
                  {vendors.map((x) => (
                    <option key={x.id} value={x.id}>{x.label}</option>
                  ))}
                </select>
              </label>
              <label className="vql-fld" style={{ marginTop: 12 }}>
                <span>{M.addTarget}</span>
                <select value={target} disabled={!drafts.data} onChange={(e) => setTarget(e.target.value)} data-testid="order-add-target">
                  <option value="">{t(M.addNew)}</option>
                  {mine.map((x) => (
                    <option key={x.id} value={x.id}>{`${t(M.alreadyDraft)}　${x.subject || "—"}`}</option>
                  ))}
                </select>
              </label>
            </>
          ) : (
            <ErrorText>{ESTIMATE_PC_MSG.requestNoVendor.replace("{customer}", customer)}</ErrorText>
          )}
        </div>
        <div className="df">
          <button type="button" className="btn quiet" onClick={onClose}>取消</button>
          <button type="submit" className="btn primary" disabled={!v} data-testid="order-add-apply">{M.addDo}</button>
        </div>
      </form>
    </div>
  );
}
